import React, { useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { useMother } from '../lib/MotherContext';
import { PROFILE_QUESTIONS, RED_FLAG_QUESTIONS } from '../data/modules';
import {
  CONCERN_OPTIONS,
  GOAL_CONFIRM,
  hasConflict,
  getAllConcernIds,
  buildDedupedFollowups,
} from '../data/combinedConcerns';
import { computeCombinedRisk } from '../data/riskLogic';
import { ChevronLeft, ChevronRight, Check, UserPlus, User, AlertTriangle } from 'lucide-react';

const STORAGE_KEY = 'app_selfcheck';
const GATE_DISMISS_KEY = 'selfcheck_gate_dismissed';

// Profile questions without the legacy main_concern picker — that is now a
// dedicated 2-step screen (primary + related) handled below.
const PROFILE_QUESTIONS_V2 = PROFILE_QUESTIONS.filter((q) => q.id !== 'main_concern');

export default function SelfCheck() {
  const { lang, t } = useLanguage();
  const navigate = useNavigate();
  const { mother } = useMother();

  const [showGate, setShowGate] = useState(() => {
    if (mother) return false;
    return sessionStorage.getItem(GATE_DISMISS_KEY) !== '1';
  });

  const [profile, setProfile] = useState({});
  const [primaryConcern, setPrimaryConcern] = useState(null);
  const [relatedConcerns, setRelatedConcerns] = useState([]);
  const [goalConfirmation, setGoalConfirmation] = useState(null);
  const [redFlags, setRedFlags] = useState({ pain_score: 0 });
  const [concernFollowups, setConcernFollowups] = useState({});

  // Phases: profile → primary → related → redflag → followups → done
  const [phase, setPhase] = useState('profile');
  const [step, setStep] = useState(0); // index within current phase

  const profileQs = PROFILE_QUESTIONS_V2;
  const redFlagQs = RED_FLAG_QUESTIONS;

  const concernIds = useMemo(
    () => getAllConcernIds({ primary_concern: primaryConcern, related_concerns: relatedConcerns }),
    [primaryConcern, relatedConcerns],
  );
  // Flatten follow-ups into (concernId, question, mirrors) tuples, with
  // duplicate questions across concerns asked only once, and questions with
  // a `condition(answers)` predicate filtered against the current answers.
  const followupPairs = useMemo(
    () => buildDedupedFollowups(concernIds, concernFollowups),
    [concernIds, concernFollowups],
  );

  const showGoalConfirm = useMemo(
    () => hasConflict(primaryConcern, relatedConcerns),
    [primaryConcern, relatedConcerns],
  );

  // Approximate total steps for the progress bar.
  const totalSteps =
    profileQs.length +
    1 /* primary */ +
    1 /* related */ +
    (showGoalConfirm ? 1 : 0) +
    redFlagQs.length +
    followupPairs.length;

  const currentIndex = (() => {
    let idx = 0;
    if (phase === 'profile') return step;
    idx += profileQs.length;
    if (phase === 'primary') return idx;
    idx += 1;
    if (phase === 'related') return idx;
    idx += 1;
    if (phase === 'goal') return idx;
    if (showGoalConfirm) idx += 1;
    if (phase === 'redflag') return idx + step;
    idx += redFlagQs.length;
    if (phase === 'followups') return idx + step;
    return totalSteps;
  })();
  const progress = Math.round(((currentIndex + 1) / Math.max(totalSteps, 1)) * 100);

  const phaseLabel =
    phase === 'profile' ? t('step_profile')
    : phase === 'primary' ? t('step_primary_concern')
    : phase === 'related' ? t('step_related_concerns')
    : phase === 'goal' ? t('step_goal_confirm')
    : phase === 'redflag' ? t('step_redflag')
    : t('step_module');

  // Validation per phase.
  const canAdvance = (() => {
    if (phase === 'profile') {
      const q = profileQs[step];
      if (!q) return false;
      const v = profile[q.id];
      if (q.type === 'slider') return v !== undefined || q.default !== undefined;
      if (q.type === 'text') return v && String(v).trim();
      return !!v;
    }
    if (phase === 'primary') return !!primaryConcern;
    if (phase === 'related') return true; // 0-2 selections allowed
    if (phase === 'goal') return !!goalConfirmation;
    if (phase === 'redflag') {
      const q = redFlagQs[step];
      const v = redFlags[q.id];
      if (q.type === 'slider') return v !== undefined || q.default !== undefined;
      return !!v;
    }
    if (phase === 'followups') {
      const p = followupPairs[step];
      const v = (concernFollowups[p.cid] || {})[p.q.id];
      return !!v;
    }
    return false;
  })();

  const goNext = () => {
    if (phase === 'profile') {
      if (step < profileQs.length - 1) return setStep((s) => s + 1);
      setPhase('primary'); setStep(0); return;
    }
    if (phase === 'primary') {
      setPhase('related'); setStep(0); return;
    }
    if (phase === 'related') {
      if (showGoalConfirm) { setPhase('goal'); setStep(0); return; }
      setPhase('redflag'); setStep(0); return;
    }
    if (phase === 'goal') {
      setPhase('redflag'); setStep(0); return;
    }
    if (phase === 'redflag') {
      if (step < redFlagQs.length - 1) return setStep((s) => s + 1);
      if (followupPairs.length === 0) return finishCheck();
      setPhase('followups'); setStep(0); return;
    }
    if (phase === 'followups') {
      if (step < followupPairs.length - 1) return setStep((s) => s + 1);
      return finishCheck();
    }
  };

  const goBack = () => {
    if (phase === 'profile') {
      if (step > 0) setStep((s) => s - 1);
      return;
    }
    if (phase === 'primary') { setPhase('profile'); setStep(profileQs.length - 1); return; }
    if (phase === 'related') { setPhase('primary'); setStep(0); return; }
    if (phase === 'goal')    { setPhase('related'); setStep(0); return; }
    if (phase === 'redflag') {
      if (step > 0) return setStep((s) => s - 1);
      if (showGoalConfirm) { setPhase('goal'); setStep(0); return; }
      setPhase('related'); setStep(0); return;
    }
    if (phase === 'followups') {
      if (step > 0) return setStep((s) => s - 1);
      setPhase('redflag'); setStep(redFlagQs.length - 1);
    }
  };

  const toggleRelated = (id) => {
    if (id === primaryConcern) return; // can't double-pick primary
    setRelatedConcerns((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 2) return prev; // cap at 2
      return [...prev, id];
    });
  };

  const setFollowup = (pair, val) => {
    setConcernFollowups((prev) => {
      const next = { ...prev };
      // Set the primary answer for the asked question
      next[pair.cid] = { ...(next[pair.cid] || {}), [pair.q.id]: val };
      // Mirror the same answer to every concern that needed this dedup_key
      for (const m of pair.mirrors || []) {
        next[m.cid] = { ...(next[m.cid] || {}), [m.qid]: val };
      }
      return next;
    });
  };

  const finishCheck = () => {
    const risk = computeCombinedRisk(redFlags, primaryConcern, relatedConcerns, concernFollowups);

    let existingKey = '';
    try {
      const prev = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
      existingKey = prev.idempotency_key || '';
    } catch {
      existingKey = '';
    }
    const idempotencyKey =
      existingKey ||
      (window.crypto?.randomUUID
        ? window.crypto.randomUUID()
        : `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`);

    const submission = {
      idempotency_key: idempotencyKey,
      mother_id: mother?.id || null,
      language: lang,
      user_role: profile.user_role,
      postpartum_age: profile.postpartum_age,
      birth_order: profile.birth_order,
      location_type: profile.location_type,
      location_text: profile.location_type_text || '',
      feeding_method: profile.feeding_method,
      // New fields
      primary_concern: primaryConcern,
      related_concerns: relatedConcerns,
      goal_confirmation: goalConfirmation,
      concern_followups: concernFollowups,
      // Backwards-compat: keep main_concern populated for old admin/log views
      main_concern: primaryConcern,
      red_flag_answers: redFlags,
      module_answers: {},
      module_id: null,
      risk_level: risk,
      result_type: 'combined',
      customer_name: mother?.name || null,
      customer_phone: mother?.phone || null,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(submission));
    navigate('/result');
  };

  if (!showGate && phase === 'profile' && profileQs.length === 0) {
    return <div className="container-app py-10">{t('loading')}</div>;
  }

  if (showGate) {
    return (
      <div className="container-app py-8 sm:py-12 max-w-lg animate-fade-in-up" data-testid="selfcheck-guest-gate">
        <div className="card-base p-6 sm:p-10">
          <div className="w-12 h-12 rounded-2xl bg-terracotta-light flex items-center justify-center mb-4">
            <User size={20} className="text-terracotta" />
          </div>
          <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-ink mb-3">
            {t('selfcheck_gate_title')}
          </h2>
          <p className="text-sm text-ink-secondary mb-6 leading-relaxed">{t('selfcheck_gate_desc')}</p>
          <div className="space-y-2">
            <Link to="/profile/start?next=/self-check" data-testid="gate-create-profile-btn" className="btn-primary w-full">
              <UserPlus size={14} className="mr-1" /> {t('selfcheck_gate_create')}
            </Link>
            <button
              type="button"
              data-testid="gate-continue-guest-btn"
              onClick={() => {
                sessionStorage.setItem(GATE_DISMISS_KEY, '1');
                setShowGate(false);
              }}
              className="btn-outline w-full"
            >
              {t('selfcheck_gate_guest')}
            </button>
            <Link to="/profile/login?next=/self-check" data-testid="gate-login-link" className="block text-center text-xs text-ink-tertiary mt-2 hover:text-terracotta">
              {t('profile_have_account')} {t('profile_login_link')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Renderers per phase ---------------------------------------------------
  const renderProfile = () => {
    const q = profileQs[step];
    if (!q) return null;
    const val = profile[q.id];
    return (
      <>
        <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-ink mb-6 leading-snug">
          {lang === 'zh' ? q.zh : q.en}
        </h2>
        {q.type === 'choice' && (
          <div className="space-y-3">
            {q.options.map((opt) => {
              const selected = val === opt.id;
              return (
                <button
                  key={opt.id}
                  onClick={() => setProfile((p) => ({ ...p, [q.id]: opt.id }))}
                  data-testid={`opt-${q.id}-${opt.id}`}
                  className={`w-full text-left p-4 sm:p-5 rounded-2xl border-2 transition-all min-h-[64px] ${selected ? 'card-selected' : 'bg-paper border-border hover:border-terracotta/50 hover:bg-terracotta-light/20'}`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-medium text-ink">{lang === 'zh' ? opt.zh : opt.en}</span>
                    {selected && <span className="w-6 h-6 rounded-full bg-terracotta flex items-center justify-center flex-shrink-0"><Check size={14} className="text-white" /></span>}
                  </div>
                </button>
              );
            })}
            {q.hasText && val && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-ink-secondary mb-2">{lang === 'zh' ? q.textZh : q.textEn}</label>
                <input
                  type="text"
                  value={profile[`${q.id}_text`] || ''}
                  onChange={(e) => setProfile((p) => ({ ...p, [`${q.id}_text`]: e.target.value }))}
                  data-testid={`text-${q.id}`}
                  className="input-base"
                  placeholder={lang === 'zh' ? '请输入...' : 'Type here...'}
                />
              </div>
            )}
          </div>
        )}
      </>
    );
  };

  const renderConcernPicker = (multi) => {
    return (
      <>
        <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-ink mb-3 leading-snug">
          {multi ? t('q_related_title') : t('q_primary_title')}
        </h2>
        <p className="text-sm text-ink-secondary mb-6 leading-relaxed">
          {multi ? t('q_related_hint') : t('q_primary_hint')}
        </p>
        <div className="space-y-3">
          {CONCERN_OPTIONS.map((opt) => {
            const disabled = multi && opt.id === primaryConcern;
            const selected = multi ? relatedConcerns.includes(opt.id) : primaryConcern === opt.id;
            const capped = multi && !selected && relatedConcerns.length >= 2;
            return (
              <button
                key={opt.id}
                disabled={disabled}
                onClick={() => {
                  if (multi) toggleRelated(opt.id);
                  else {
                    setPrimaryConcern(opt.id);
                    setRelatedConcerns((prev) => prev.filter((x) => x !== opt.id));
                  }
                }}
                data-testid={`${multi ? 'related' : 'primary'}-${opt.id}`}
                className={`w-full text-left p-4 sm:p-5 rounded-2xl border-2 transition-all min-h-[64px] ${selected ? 'card-selected' : 'bg-paper border-border hover:border-terracotta/50 hover:bg-terracotta-light/20'} ${disabled ? 'opacity-40 cursor-not-allowed' : ''} ${capped ? 'opacity-50' : ''}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="font-medium text-ink">{lang === 'zh' ? opt.zh : opt.en}</span>
                  {selected && <span className="w-6 h-6 rounded-full bg-terracotta flex items-center justify-center flex-shrink-0"><Check size={14} className="text-white" /></span>}
                  {disabled && <span className="text-[10px] uppercase tracking-wider text-ink-tertiary">{t('q_already_primary')}</span>}
                </div>
              </button>
            );
          })}
        </div>
        {multi && (
          <p className="text-xs text-ink-tertiary mt-4" data-testid="related-cap-hint">
            {t('q_related_cap').replace('{n}', relatedConcerns.length)}
          </p>
        )}
      </>
    );
  };

  const renderGoalConfirm = () => {
    return (
      <>
        <div className="flex items-start gap-3 mb-6 p-4 rounded-2xl bg-amber-50 border border-amber-200" data-testid="goal-conflict-banner">
          <AlertTriangle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-900 leading-relaxed">{t('q_goal_conflict_note')}</p>
        </div>
        <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-ink mb-6 leading-snug">
          {lang === 'zh' ? GOAL_CONFIRM.zh : GOAL_CONFIRM.en}
        </h2>
        <div className="space-y-3">
          {GOAL_CONFIRM.options.map((opt) => {
            const selected = goalConfirmation === opt.id;
            return (
              <button
                key={opt.id}
                onClick={() => setGoalConfirmation(opt.id)}
                data-testid={`goal-${opt.id}`}
                className={`w-full text-left p-4 sm:p-5 rounded-2xl border-2 transition-all min-h-[64px] ${selected ? 'card-selected' : 'bg-paper border-border hover:border-terracotta/50 hover:bg-terracotta-light/20'}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="font-medium text-ink">{lang === 'zh' ? opt.zh : opt.en}</span>
                  {selected && <span className="w-6 h-6 rounded-full bg-terracotta flex items-center justify-center flex-shrink-0"><Check size={14} className="text-white" /></span>}
                </div>
              </button>
            );
          })}
        </div>
      </>
    );
  };

  const renderRedFlag = () => {
    const q = redFlagQs[step];
    if (!q) return null;
    const val = redFlags[q.id];
    return (
      <>
        <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-ink mb-6 leading-snug">
          {lang === 'zh' ? q.zh : q.en}
        </h2>
        {q.type === 'choice' && (
          <div className="space-y-3">
            {q.options.map((opt) => {
              const selected = val === opt.id;
              return (
                <button
                  key={opt.id}
                  onClick={() => setRedFlags((p) => ({ ...p, [q.id]: opt.id }))}
                  data-testid={`opt-${q.id}-${opt.id}`}
                  className={`w-full text-left p-4 sm:p-5 rounded-2xl border-2 transition-all min-h-[64px] ${selected ? 'card-selected' : 'bg-paper border-border hover:border-terracotta/50 hover:bg-terracotta-light/20'}`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-medium text-ink">{lang === 'zh' ? opt.zh : opt.en}</span>
                    {selected && <span className="w-6 h-6 rounded-full bg-terracotta flex items-center justify-center flex-shrink-0"><Check size={14} className="text-white" /></span>}
                  </div>
                </button>
              );
            })}
          </div>
        )}
        {q.type === 'slider' && (
          <div className="py-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-ink-tertiary">{q.min}</span>
              <span className="font-heading text-5xl font-semibold text-terracotta" data-testid="slider-value">
                {val ?? q.default}
              </span>
              <span className="text-sm text-ink-tertiary">{q.max}</span>
            </div>
            <input
              type="range"
              min={q.min}
              max={q.max}
              value={val ?? q.default}
              onChange={(e) => setRedFlags((p) => ({ ...p, [q.id]: Number(e.target.value) }))}
              data-testid={`slider-${q.id}`}
              className="w-full h-2 bg-border rounded-full appearance-none accent-terracotta"
            />
          </div>
        )}
      </>
    );
  };

  const renderFollowup = () => {
    const pair = followupPairs[step];
    if (!pair) return null;
    const { cid, q, mirrors } = pair;
    const opt = CONCERN_OPTIONS.find((o) => o.id === cid);
    const concernLabel = opt ? (lang === 'zh' ? opt.zh : opt.en) : cid;
    const val = (concernFollowups[cid] || {})[q.id];
    // Show "applies to: X, Y" hint when this dedup'd question covers multiple concerns
    const mirroredLabels = (mirrors || [])
      .map((m) => CONCERN_OPTIONS.find((o) => o.id === m.cid))
      .filter(Boolean)
      .map((o) => (lang === 'zh' ? o.zh : o.en));
    return (
      <>
        <div className="mb-3 flex items-center gap-2 flex-wrap">
          <span className="text-[11px] uppercase tracking-[0.18em] font-semibold text-terracotta">
            {concernLabel}
          </span>
          {mirroredLabels.length > 0 && (
            <span className="text-[11px] text-ink-tertiary" data-testid="followup-applies-hint">
              · {t('q_followup_applies_to')}: {mirroredLabels.join(', ')}
            </span>
          )}
        </div>
        <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-ink mb-6 leading-snug">
          {lang === 'zh' ? q.zh : q.en}
        </h2>
        <div className="space-y-3">
          {q.options.map((o) => {
            const selected = val === o.id;
            return (
              <button
                key={o.id}
                onClick={() => setFollowup(pair, o.id)}
                data-testid={`followup-${cid}-${q.id}-${o.id}`}
                className={`w-full text-left p-4 sm:p-5 rounded-2xl border-2 transition-all min-h-[64px] ${selected ? 'card-selected' : 'bg-paper border-border hover:border-terracotta/50 hover:bg-terracotta-light/20'}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="font-medium text-ink">{lang === 'zh' ? o.zh : o.en}</span>
                  {selected && <span className="w-6 h-6 rounded-full bg-terracotta flex items-center justify-center flex-shrink-0"><Check size={14} className="text-white" /></span>}
                </div>
              </button>
            );
          })}
        </div>
      </>
    );
  };

  return (
    <div className="container-app py-8 sm:py-12 max-w-2xl" data-testid="self-check-page">
      {/* Progress */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <span className="text-xs uppercase tracking-wider font-semibold text-terracotta">{phaseLabel}</span>
          <span className="text-xs text-ink-tertiary" data-testid="progress-label">{currentIndex + 1} / {totalSteps}</span>
        </div>
        <div className="w-full bg-border rounded-full h-2">
          <div className="bg-terracotta h-2 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} data-testid="progress-bar" />
        </div>
      </div>

      <div className="card-base p-6 sm:p-10 animate-fade-in-up" key={`${phase}-${step}`}>
        {phase === 'profile' && renderProfile()}
        {phase === 'primary' && renderConcernPicker(false)}
        {phase === 'related' && renderConcernPicker(true)}
        {phase === 'goal' && renderGoalConfirm()}
        {phase === 'redflag' && renderRedFlag()}
        {phase === 'followups' && renderFollowup()}

        <div className="flex gap-3 mt-8">
          <button
            onClick={goBack}
            disabled={phase === 'profile' && step === 0}
            data-testid="back-btn"
            className="btn-outline flex-1 sm:flex-none disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ChevronLeft size={18} className="mr-1" /> {t('back')}
          </button>
          <button
            onClick={goNext}
            disabled={!canAdvance}
            data-testid="next-btn"
            className="btn-primary flex-1 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {t('next')} <ChevronRight size={18} className="ml-1" />
          </button>
        </div>
      </div>
    </div>
  );
}
